// Acompanhamento de Líderes (follow-up 24h líder × lead) — painel da diretoria,
// admin-only. ADITIVO ao relatório de Marketing (que segue intacto). Lê
// /api/leader-followups (atribuições + estado do relógio + conversão Oracle ao vivo)
// e mostra o mapa líder×lead com countdown de 24h, desfecho e timeline.
// Desenho em vault/conhecimento/acompanhamento-lideres-followup.md.
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiAward, FiRefreshCw, FiClock, FiCheck, FiX, FiAlertTriangle,
  FiChevronDown, FiChevronRight, FiTrendingUp, FiAlertCircle, FiInfo, FiRepeat, FiSend, FiSearch,
  FiMapPin, FiGrid, FiDownload, FiLoader,
} from 'react-icons/fi'
import AssignLeaderModal from './AssignLeaderModal'
import { LeaderStageControl, type StageReply } from './LeaderStageControl'
import { type LeaderPerformance, filterPerf, buildReportHtml } from './MetaAdsReport'

interface FollowupItem {
  assignmentId: string
  conversationId: string
  deviceId: string
  leaderCode: string | null
  leaderName: string
  distrito: string
  grupo: string
  cidade: string
  leadName: string
  leadPhone: string
  assignedAt: string | null
  deadlineAt: string | null
  hoursLeft: number | null
  status: 'open' | 'acknowledged' | 'overdue' | 'escalated' | 'concluded'
  leaderOutcome: 'success' | 'failure' | 'trying' | null
  leaderOutcomeAt: string | null
  leaderOutcomeReason: string | null
  responseHours: number | null
  convertedOracle: boolean
  nudgeCount: number
  nextNudgeAt: string | null
  deadlineNotifiedAt: string | null
  autoExpiry: boolean
  hasFollowupRow: boolean
  // Fluxo v2 em 3 etapas (null = fluxo antigo do botão único "Deu certo")
  stage: number | null
  contacted: 'yes' | 'pending' | 'unreachable' | null
  explained: 'yes' | 'partial' | 'refused' | null
  interest: 'yes' | 'thinking' | 'no' | null
  // Tier de responsividade (frente 5 do Pareto). C = sweeper suprime a
  // cobrança automática — a pill de countdown vira aviso de pausa.
  tier: 'A' | 'B' | 'C' | null
}

// Chip do funil v2: mostra em que etapa o follow-up está e o que a líder já
// respondeu. Só renderiza pra itens do fluxo novo (stage != null).
function StageChip({ it }: { it: FollowupItem }) {
  if (it.stage == null) return null
  if (it.contacted === 'unreachable') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" title="Líder informou que o número do lead não atende">Nº não atende</span>
  }
  const steps = [
    { done: it.contacted === 'yes', label: 'falou' },
    { done: it.explained === 'yes' || it.explained === 'partial', label: 'explicou' },
    { done: it.interest === 'yes', label: 'interesse' },
  ]
  const doneCount = steps.filter(s => s.done).length
  const detail = [
    `Contato: ${it.contacted === 'yes' ? 'falou com o lead' : it.contacted === 'pending' ? 'ainda tentando' : 'aguardando'}`,
    `Explicação: ${it.explained === 'yes' ? 'apresentou' : it.explained === 'partial' ? 'só por cima' : it.explained === 'refused' ? 'lead não quis ouvir' : 'aguardando'}`,
    `Interesse: ${it.interest === 'yes' ? 'quer se cadastrar' : it.interest === 'thinking' ? 'ficou de pensar' : it.interest === 'no' ? 'sem interesse' : 'aguardando'}`,
  ].join(' · ')
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
      doneCount >= 3 ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
      : doneCount > 0 ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30'
      : 'bg-muted text-muted-foreground border-border'
    }`} title={detail}>
      {doneCount}/3 etapas
    </span>
  )
}
interface LeaderGroup {
  leaderCode: string | null
  leaderId: string | null
  leaderName: string
  distrito: string
  grupo: string
  cidade: string
  tier: 'A' | 'B' | 'C' | null
  tierRate: number | null
  tierSample: number | null
  leads: number
  responded: number
  converted: number
  overdue: number
  responsePct: number
  items: FollowupItem[]
}
// Hierarquia de exibição (pedido do usuário 2026-07-06): Distrito → Grupo →
// Líder → Consultor/lead, pra ficar visualmente fácil de entender a estrutura
// organizacional. Montada em cima de `leaders` (já vem plano do backend).
interface GrupoGroup {
  distrito: string; grupo: string
  leads: number; responded: number; converted: number; overdue: number; responsePct: number
  leaders: LeaderGroup[]
}
interface DistritoGroup {
  distrito: string
  leads: number; responded: number; converted: number; overdue: number; responsePct: number
  grupos: GrupoGroup[]
}

function buildHierarchy(leaders: LeaderGroup[]): DistritoGroup[] {
  const byDistrito = new Map<string, LeaderGroup[]>()
  for (const l of leaders) {
    const d = l.distrito || '(sem distrito)'
    if (!byDistrito.has(d)) byDistrito.set(d, [])
    byDistrito.get(d)!.push(l)
  }
  const distritos: DistritoGroup[] = []
  for (const [distrito, ls] of byDistrito) {
    const byGrupo = new Map<string, LeaderGroup[]>()
    for (const l of ls) {
      const g = l.grupo || '(sem grupo)'
      if (!byGrupo.has(g)) byGrupo.set(g, [])
      byGrupo.get(g)!.push(l)
    }
    const grupos: GrupoGroup[] = [...byGrupo.entries()].map(([grupo, gls]) => {
      const leads = gls.reduce((s, l) => s + l.leads, 0)
      const responded = gls.reduce((s, l) => s + l.responded, 0)
      const converted = gls.reduce((s, l) => s + l.converted, 0)
      const overdue = gls.reduce((s, l) => s + l.overdue, 0)
      return { distrito, grupo, leads, responded, converted, overdue, responsePct: leads ? Math.round((responded / leads) * 100) : 0, leaders: gls }
    }).sort((a, b) => a.grupo.localeCompare(b.grupo))
    const leads = grupos.reduce((s, g) => s + g.leads, 0)
    const responded = grupos.reduce((s, g) => s + g.responded, 0)
    const converted = grupos.reduce((s, g) => s + g.converted, 0)
    const overdue = grupos.reduce((s, g) => s + g.overdue, 0)
    distritos.push({ distrito, leads, responded, converted, overdue, responsePct: leads ? Math.round((responded / leads) * 100) : 0, grupos })
  }
  return distritos.sort((a, b) => a.distrito.localeCompare(b.distrito))
}

interface Kpis {
  total: number; open: number; respondedPct: number; onTimePct: number
  convertedPct: number; converted: number; avgResponseHours: number | null; overdue: number
}
interface FollowupEvent { id: number; ts: string; type: string; detail: string | null; actor: string | null }

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const daysAgo = (n: number) => isoDate(new Date(Date.now() - n * 86400000))

const STATUS_META: Record<FollowupItem['status'], { label: string; cls: string }> = {
  open:         { label: 'Aguardando',  cls: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30' },
  acknowledged: { label: 'Em contato',  cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  overdue:      { label: 'Atrasado',    cls: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30' },
  escalated:    { label: 'Escalado',    cls: 'bg-red-600/20 text-red-700 dark:text-red-300 border-red-600/40' },
  concluded:    { label: 'Concluído',   cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
}
const OUTCOME_META: Record<string, { label: string; cls: string }> = {
  success: { label: 'Deu certo',      cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  failure: { label: 'Não deu certo',  cls: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30' },
  trying:  { label: 'Ainda tentando', cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' },
}
const EVENT_LABEL: Record<string, string> = {
  created: 'Encaminhado à líder', reassigned: 'Reatribuído', nudge_sent: 'Lembrete enviado',
  button_reply: 'Resposta da líder', text_reply: 'Resposta em texto', manual_update: 'Ajuste manual',
  escalated: 'Escalado à diretoria', converted: 'Lead convertido',
  deadline_expired_sent: 'Aviso de prazo expirado enviado',
}

const initials = (name: string) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'

const fmtDateTime = (ts: string | null) => ts ? new Date(ts).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande' }) : '—'

// Formata uma duração (ms) em "Xh" ou "Xmin".
const fmtDur = (ms: number) => { const h = ms / 3600000; return h >= 1 ? `${Math.round(h)}h` : `${Math.max(1, Math.round(h * 60))}min` }

// Próxima ação automática de um lead aguardando: lembrete (12h) → aviso de prazo (24h)
// → expirado. Reflete o que o sweeper vai disparar a seguir. Reusado pelo painel e
// (mesma lógica) pela sidebar. Retorna null pra estados sem próxima ação.
function nextFollowupAction(item: { status: string; nudgeCount: number; nextNudgeAt: string | null; deadlineAt: string | null; deadlineNotifiedAt: string | null }): { text: string; cls: string } | null {
  if (item.status !== 'open' && item.status !== 'overdue') return null
  const now = Date.now()
  const nudgeMs = item.nextNudgeAt ? new Date(item.nextNudgeAt).getTime() : null
  const deadlineMs = item.deadlineAt ? new Date(item.deadlineAt).getTime() : null
  if (item.nudgeCount === 0 && nudgeMs && nudgeMs > now) {
    const d = nudgeMs - now
    return { text: `lembrete em ${fmtDur(d)}`, cls: d < 3 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  if (deadlineMs && deadlineMs > now) {
    const d = deadlineMs - now
    return { text: `aviso de prazo em ${fmtDur(d)}`, cls: d < 6 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  return { text: item.deadlineNotifiedAt ? 'prazo expirado · líder avisada' : 'prazo expirado', cls: 'text-red-600 dark:text-red-400' }
}

// Pill da próxima ação automática (lembrete → aviso de prazo → expirado).
function CountdownPill({ item }: { item: FollowupItem }) {
  // 'acknowledged' = líder respondeu "tentando" → o prazo de 24h não corre mais;
  // mostra o estado em vez do countdown (senão "atrasado" contradiz o "Em contato").
  if (item.status === 'acknowledged') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
        <FiClock className="w-3 h-3" /> ainda tentando
      </span>
    )
  }
  // Líder tier C: o sweeper NÃO manda lembrete/aviso automático (frente 5 do
  // Pareto) — mostrar countdown aqui prometeria uma cobrança que nunca sai.
  if (item.tier === 'C' && (item.status === 'open' || item.status === 'overdue')) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400"
        title="Líder tier C (não responde às cobranças): lembrete e aviso de prazo automáticos suspensos pra proteger a qualidade do número. Cobre manualmente ou troque a líder.">
        <FiAlertTriangle className="w-3 h-3" /> cobrança automática pausada
      </span>
    )
  }
  const action = nextFollowupAction(item)
  if (!action) return null
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium tabular-nums ${action.cls}`}
      title={item.deadlineAt ? `Prazo final: ${fmtDateTime(item.deadlineAt)}` : ''}>
      <FiClock className="w-3 h-3" />
      {action.text}
    </span>
  )
}

export function LeaderFollowupPanel() {
  const { authenticatedFetch } = useAuth()
  const [loading, setLoading] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [kpis, setKpis] = useState<Kpis | null>(null)
  const [leaders, setLeaders] = useState<LeaderGroup[]>([])

  const [days, setDays] = useState(30)
  const [statusFilter, setStatusFilter] = useState<'' | FollowupItem['status']>('')
  const [onlyOverdue, setOnlyOverdue] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Herdados do painel Relatório (fundido aqui em 2026-07-10): filtro de cidade
  // da líder + inclusão das líderes do roster sem lead no período (ociosidade).
  const [filtroCidade, setFiltroCidade] = useState('')
  const [includeIdle, setIncludeIdle] = useState(false)

  // Exportar PDF (também herdado do Relatório): o modal recorta o escopo
  // (cidade/distrito/grupo/líder/período) e gera a versão imprimível via
  // /api/marketing/leader-performance + buildReportHtml — mesma máquina de
  // antes, só carregada sob demanda ao abrir o modal.
  const [showExport, setShowExport] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [perf, setPerf] = useState<LeaderPerformance | null>(null)
  // Tipo do PDF: 'lideres' = desempenho das líderes (escopo recortável);
  // 'anuncios' = anúncio → CRM lead a lead (só período). O segundo migrou do
  // painel Marketing em 15/07 e foi fundido neste modal (2 botões confundiam).
  const [expTipo, setExpTipo] = useState<'lideres' | 'anuncios'>('lideres')
  const [perfLoading, setPerfLoading] = useState(false)
  const [expCidade, setExpCidade] = useState('')
  const [expDistrito, setExpDistrito] = useState('')
  const [expGrupo, setExpGrupo] = useState('')
  const [expLeaderId, setExpLeaderId] = useState('')
  const [expFrom, setExpFrom] = useState(daysAgo(30))
  const [expTo, setExpTo] = useState(isoDate(new Date()))
  // Formato mobile (página vertical estreita, leads em cards) — padrão da
  // diretoria, que lê no celular. Desmarcado = A4 retrato pra impressão.
  const [expMobile, setExpMobile] = useState(true)

  // Drawer de detalhe
  const [detailItem, setDetailItem] = useState<FollowupItem | null>(null)
  const [detailEvents, setDetailEvents] = useState<FollowupEvent[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  // Ações manuais (Fase 3)
  const [actionNote, setActionNote] = useState('')
  const [actionBusy, setActionBusy] = useState(false)
  // Reatribuição: abre o AssignLeaderModal (mesmo do inbox) pra a líder que não
  // retornou ser trocada por outra. O handoff trata reassign (reseta relógio, evento).
  const [reassignFor, setReassignFor] = useState<FollowupItem | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const qs = new URLSearchParams({ from: daysAgo(days), to: isoDate(new Date()) })
      if (statusFilter) qs.set('status', statusFilter)
      if (onlyOverdue) qs.set('overdue', '1')
      if (includeIdle) qs.set('includeIdle', '1')
      const r = await authenticatedFetch(`/api/leader-followups?${qs.toString()}`)
      if (r.status === 403) { setForbidden(true); return }
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Erro ao carregar')
      setKpis(d.kpis); setLeaders(d.leaders || [])
    } catch (e: any) {
      setError(e.message || 'Erro')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, days, statusFilter, onlyOverdue, includeIdle])

  useEffect(() => { load() }, [load])
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t) }, [load])

  const refetchEvents = useCallback(async (assignmentId: string) => {
    setDetailLoading(true)
    try {
      const r = await authenticatedFetch(`/api/leader-followups/${encodeURIComponent(assignmentId)}`)
      const d = await r.json()
      if (r.ok && d.success) setDetailEvents(d.events || [])
    } catch { /* silencioso */ } finally {
      setDetailLoading(false)
    }
  }, [authenticatedFetch])

  const openDetail = useCallback(async (item: FollowupItem) => {
    setDetailItem(item); setDetailEvents([]); setActionNote('')
    await refetchEvents(item.assignmentId)
  }, [refetchEvents])

  // POST de ação manual + patch otimista no item aberto + refresh (eventos + lista).
  const postAction = useCallback(async (path: string, body: any, patch?: Partial<FollowupItem>) => {
    if (!detailItem) return
    setActionBusy(true); setError(''); setInfo('')
    try {
      const r = await authenticatedFetch(`/api/leader-followups/${encodeURIComponent(detailItem.assignmentId)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.success === false) throw new Error(d.error || 'Erro na ação')
      // Cobrança/aviso vão pra fila throttada (3 por líder / 2 min) — feedback explícito.
      if (path === 'resend-nudge' || path === 'notify-expired') {
        setInfo(d.alreadyQueued ? 'Já estava na fila.'
          : path === 'notify-expired' ? 'Aviso de prazo enfileirado — sai em instantes.'
          : 'Cobrança enfileirada — sai em instantes (3 por líder a cada 2 min).')
        setTimeout(() => setInfo(''), 5000)
      }
      if (patch) setDetailItem(prev => prev ? { ...prev, ...patch } : prev)
      // stage-manual devolve o followup atualizado (snake_case) — sem aplicar no
      // detailItem o drawer ficava preso na etapa antiga e o próximo clique dava
      // 409 "registre-a primeiro" (achado do pente-fino externo de 2026-07-10).
      if (d.followup) setDetailItem(prev => prev ? {
        ...prev,
        stage: d.followup.stage == null ? null : Number(d.followup.stage),
        contacted: d.followup.contacted ?? null,
        explained: d.followup.explained ?? null,
        interest: d.followup.interest ?? null,
        status: d.followup.status || prev.status,
        leaderOutcome: d.followup.leader_outcome ?? prev.leaderOutcome,
      } : prev)
      await refetchEvents(detailItem.assignmentId)
      load()
    } catch (e: any) {
      setError(e.message || 'Erro')
    } finally {
      setActionBusy(false)
    }
  }, [authenticatedFetch, detailItem, refetchEvents, load])

  // Registro manual por etapa — alimenta a MESMA máquina de estados dos botões
  // do WhatsApp (substituiu o markOutcome de desfecho único em 2026-07-10).
  const markStage = (reply: StageReply) => postAction('stage-manual', { reply })
  const saveNote = () => { if (actionNote.trim()) postAction('note', { note: actionNote.trim() }).then(() => setActionNote('')) }

  // Abre o modal de export já buscando a performance (fonte do PDF) pro período
  // em tela — os selects encadeados do recorte derivam dela.
  const openExport = async () => {
    const f = daysAgo(days), t = isoDate(new Date())
    setExpFrom(f); setExpTo(t)
    setExpTipo('lideres')
    setExpCidade(filtroCidade); setExpDistrito(''); setExpGrupo(''); setExpLeaderId('')
    setShowExport(true)
    setPerfLoading(true)
    try {
      const r = await authenticatedFetch(`/api/marketing/leader-performance?from=${f}&to=${t}${includeIdle ? '&includeIdle=1' : ''}`)
      const d = await r.json()
      setPerf(d && d.success ? d : null)
    } catch { setPerf(null) } finally { setPerfLoading(false) }
  }

  const generateExport = async () => {
    setExporting(true)
    try {
      // Tipo "anúncios": relatório anúncio → CRM (Meta Ads lead a lead),
      // mesmo gerador que vivia no botão "Relatório completo". Só usa período.
      if (expTipo === 'anuncios') {
        const r = await authenticatedFetch(`/api/meta-ads/report?from=${expFrom}&to=${expTo}`)
        const j = await r.json()
        if (!j || !j.success) { alert('Sem dados de Meta Ads para o período escolhido.'); return }
        const html = buildReportHtml(j, null, { includeMetaAds: true })
        const win = window.open('', '_blank')
        if (!win) { alert('Permita pop-ups para exportar o relatório.'); return }
        win.document.write(html); win.document.close()
        setShowExport(false)
        return
      }
      let p = perf
      // Refetch só se o período do modal divergir do que foi carregado ao abrir.
      if (expFrom !== daysAgo(days) || expTo !== isoDate(new Date())) {
        const r = await authenticatedFetch(`/api/marketing/leader-performance?from=${expFrom}&to=${expTo}${includeIdle ? '&includeIdle=1' : ''}`)
        const j = await r.json()
        p = j && j.success ? j : null
      }
      if (!p) { alert('Sem dados de líderes para o período escolhido.'); return }
      const pFiltered = filterPerf(p, {
        cidade: expCidade || undefined,
        distrito: expDistrito || undefined,
        grupo: expGrupo || undefined,
        leaderId: expLeaderId || undefined,
      })
      const leaderName = expLeaderId ? (pFiltered.porLider.find(l => l.leaderId === expLeaderId)?.leaderName || '') : ''
      const scopeLabel = [expCidade, expDistrito, expGrupo, leaderName].filter(Boolean).join(' › ')
      // buildReportHtml com includeMetaAds:false só usa range de `data`; o
      // conteúdo (capa + capítulos) vem todo de `perf`. Por isso o data mínimo.
      const minimalData: any = { success: true, range: { from: expFrom, to: expTo }, ads: [], leads: [], summary: {} }
      const html = buildReportHtml(minimalData, pFiltered, { includeMetaAds: false, scopeLabel, mobile: expMobile })
      const win = window.open('', '_blank')
      if (!win) { alert('Permita pop-ups para exportar o relatório.'); return }
      win.document.write(html); win.document.close()
      setShowExport(false)
    } catch (e: any) {
      alert('Erro ao gerar o relatório: ' + (e?.message || 'falha de rede'))
    } finally { setExporting(false) }
  }

  // Selects encadeados do modal de export, derivados da performance carregada.
  const expCidades = perf ? Array.from(new Set(perf.porLider.map(l => l.cidade))).sort() : []
  const expDistritos = perf
    ? Array.from(new Set((expCidade ? perf.porLider.filter(l => l.cidade === expCidade) : perf.porLider).map(l => l.distrito))).sort()
    : []
  const expGrupos = (() => {
    if (!perf) return []
    let base = perf.porLider
    if (expCidade) base = base.filter(l => l.cidade === expCidade)
    if (expDistrito) base = base.filter(l => l.distrito === expDistrito)
    return Array.from(new Set(base.map(l => l.grupo))).sort()
  })()
  const expLideres = (() => {
    if (!perf) return []
    let base = perf.porLider
    if (expCidade) base = base.filter(l => l.cidade === expCidade)
    if (expDistrito) base = base.filter(l => l.distrito === expDistrito)
    if (expGrupo) base = base.filter(l => l.grupo === expGrupo)
    return base.slice().sort((a, b) => a.leaderName.localeCompare(b.leaderName))
  })()

  const toggleKey = (key: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })

  if (forbidden) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FiAward className="w-7 h-7 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Painel restrito — peça acesso ao administrador.</p>
        </div>
      </div>
    )
  }

  const kpiCards = kpis ? [
    { label: 'Em aberto', value: kpis.open, icon: FiClock, cls: 'text-sky-500' },
    { label: 'Respondidos', value: `${kpis.respondedPct}%`, icon: FiCheck, cls: 'text-emerald-500' },
    { label: 'No prazo', value: `${kpis.onTimePct}%`, icon: FiCheck, cls: 'text-emerald-500' },
    { label: 'Convertidos', value: `${kpis.convertedPct}%`, icon: FiTrendingUp, cls: 'text-purple-500' },
    { label: 'Tempo médio resp.', value: kpis.avgResponseHours != null ? `${kpis.avgResponseHours}h` : '—', icon: FiClock, cls: 'text-muted-foreground' },
    { label: 'Atrasados', value: kpis.overdue, icon: FiAlertTriangle, cls: kpis.overdue ? 'text-red-500' : 'text-muted-foreground' },
  ] : []

  // Filtro de cidade (da líder) aplicado antes da busca e da hierarquia.
  const cidades = Array.from(new Set(leaders.map(l => l.cidade).filter(Boolean))).sort()
  const cityLeaders = filtroCidade ? leaders.filter(g => g.cidade === filtroCidade) : leaders

  // Busca client-side: casa lead, líder, distrito/grupo (texto) e telefone (só dígitos).
  // Se a líder casa, mantém todos os leads dela; senão filtra os leads que casam.
  // Líder ociosa (0 items) sobrevive à busca quando o próprio nome dela casa.
  const q = search.trim().toLowerCase()
  const qDigits = q.replace(/\D/g, '')
  const displayLeaders = !q ? cityLeaders : cityLeaders
    .map(g => {
      const leaderHit = `${g.leaderName} ${g.distrito} ${g.grupo}`.toLowerCase().includes(q)
      const items = leaderHit ? g.items : g.items.filter(it =>
        (it.leadName || '').toLowerCase().includes(q) ||
        (it.leaderName || '').toLowerCase().includes(q) ||
        (it.distrito || '').toLowerCase().includes(q) ||
        (it.grupo || '').toLowerCase().includes(q) ||
        (qDigits.length >= 3 && (it.leadPhone || '').replace(/\D/g, '').includes(qDigits))
      )
      return { ...g, items }
    })
    .filter(g => g.items.length > 0 || `${g.leaderName} ${g.distrito} ${g.grupo}`.toLowerCase().includes(q))
  const displayDistritos = buildHierarchy(displayLeaders)

  return (
    <div className="h-full flex flex-col overflow-hidden neu-forms">
      {/* Header + KPIs */}
      <div className="flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <FiAward className="w-5 h-5 text-purple-500" />
          <h2 className="text-base font-semibold text-foreground">Acompanhamento</h2>
          <span className="text-xs text-muted-foreground hidden sm:inline">· retorno do lead em até 24h</span>
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={openExport}
              title="Exportar PDF — escolha o escopo (cidade/distrito/grupo/líder/período) antes de gerar a versão imprimível"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11px] font-medium bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity">
              <FiDownload className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Exportar PDF</span>
            </button>
            <button onClick={load} disabled={loading} title="Atualizar"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {kpiCards.map(k => (
            <div key={k.label} className="rounded-lg border border-border bg-background neu-card px-3 py-2">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <k.icon className={`w-3.5 h-3.5 ${k.cls}`} />
                <span className="truncate">{k.label}</span>
              </div>
              <div className={`text-lg font-bold tabular-nums ${k.cls}`}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-2 flex flex-wrap items-center gap-2">
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}
          className="h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground">
          <option value="">Todos os status</option>
          <option value="open">Aguardando</option>
          <option value="acknowledged">Em contato</option>
          <option value="overdue">Atrasado</option>
          <option value="escalated">Escalado</option>
          <option value="concluded">Concluído</option>
        </select>
        <select value={filtroCidade} onChange={e => setFiltroCidade(e.target.value)}
          title="Filtrar pela cidade da líder"
          className="h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground">
          <option value="">Todas as cidades</option>
          {cidades.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => setOnlyOverdue(v => !v)}
          className={`h-8 px-2.5 text-xs rounded-md border inline-flex items-center gap-1.5 transition-colors ${
            onlyOverdue ? 'neu-relief bg-red-500/15 border-red-500/40 text-red-600 dark:text-red-400' : 'neu-convex border-border text-muted-foreground hover:bg-muted/50'
          }`}>
          <FiAlertTriangle className="w-3.5 h-3.5" /> Só atrasados
        </button>
        <label className="h-8 px-2.5 text-xs rounded-md border border-border neu-convex inline-flex items-center gap-1.5 text-muted-foreground cursor-pointer select-none hover:bg-muted/50 transition-colors"
          title="Inclui líderes do cadastro que não receberam nenhum lead no período (mostra ociosidade)">
          <input type="checkbox" checked={includeIdle} onChange={e => setIncludeIdle(e.target.checked)}
            className="accent-purple-600" />
          Incluir ociosas
        </label>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar lead, líder, telefone…"
            className="w-full h-8 text-xs rounded-md border border-border bg-background pl-7 pr-7 text-foreground placeholder:text-muted-foreground/70"
          />
          {search && (
            <button onClick={() => setSearch('')} title="Limpar busca"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {error && (
          <span className="inline-flex items-center gap-1.5 text-xs text-destructive ml-auto">
            <FiAlertCircle className="w-3.5 h-3.5" /> {error}
          </span>
        )}
        {info && !error && (
          <span className="inline-flex items-center gap-1.5 text-xs text-sky-600 dark:text-sky-400 ml-auto">
            <FiSend className="w-3.5 h-3.5" /> {info}
          </span>
        )}
      </div>

      {/* Hierarquia Distrito → Grupo → Líder → Consultor/lead */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {displayDistritos.length === 0 && !loading && (
          <div className="text-center text-muted-foreground py-12">
            <FiAward className="w-9 h-9 mx-auto mb-3 opacity-40" />
            <p className="text-sm">{q ? `Nenhum resultado para "${search.trim()}".` : 'Nenhuma atribuição de líder no período.'}</p>
          </div>
        )}
        {displayDistritos.map(d => {
          const dKey = `d:${d.distrito}`
          const dOpen = q ? true : expanded.has(dKey)
          return (
            <div key={dKey} className="rounded-lg border border-border bg-card neu-card overflow-hidden">
              {/* Cabeçalho do distrito */}
              <button onClick={() => toggleKey(dKey)}
                className="w-full flex items-center gap-3 px-3 py-2.5 neu-rule-1 hover:bg-muted/40 transition-colors text-left">
                {dOpen ? <FiChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                <FiMapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-foreground truncate">{d.distrito}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[11px]">
                  <span className="text-muted-foreground" title="Leads recebidos no período">{d.leads} lead{d.leads === 1 ? '' : 's'}</span>
                  <span className="text-emerald-600 dark:text-emerald-400" title="Taxa de resposta">{d.responsePct}%</span>
                  {d.converted > 0 && <span className="text-purple-600 dark:text-purple-400" title="Convertidos (Oracle)">✦ {d.converted}</span>}
                  {d.overdue > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title="Atrasados/escalados">
                      <FiAlertTriangle className="w-3 h-3" />{d.overdue}
                    </span>
                  )}
                </div>
              </button>

              {/* Grupos do distrito */}
              {dOpen && (
                <div className="border-t border-border divide-y divide-border">
                  {d.grupos.map(g => {
                    const gKey = `g:${d.distrito}|${g.grupo}`
                    const gOpen = q ? true : expanded.has(gKey)
                    return (
                      <div key={gKey}>
                        <button onClick={() => toggleKey(gKey)}
                          className="w-full flex items-center gap-3 px-3 py-2 pl-8 neu-rule-2 hover:bg-muted/30 transition-colors text-left">
                          {gOpen ? <FiChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          <FiGrid className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-foreground truncate">{g.grupo}</div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0 text-[11px]">
                            <span className="text-muted-foreground" title="Leads recebidos no período">{g.leads} lead{g.leads === 1 ? '' : 's'}</span>
                            <span className="text-emerald-600 dark:text-emerald-400" title="Taxa de resposta">{g.responsePct}%</span>
                            {g.converted > 0 && <span className="text-purple-600 dark:text-purple-400" title="Convertidos (Oracle)">✦ {g.converted}</span>}
                            {g.overdue > 0 && (
                              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title="Atrasados/escalados">
                                <FiAlertTriangle className="w-3 h-3" />{g.overdue}
                              </span>
                            )}
                          </div>
                        </button>

                        {/* Líderes do grupo */}
                        {gOpen && (
                          <div className="divide-y divide-border">
                            {g.leaders.map(lg => {
                              const lKey = lg.leaderCode || lg.leaderId || lg.leaderName
                              const lOpen = q ? true : expanded.has(lKey)
                              return (
                                <div key={lKey}>
                                  <button onClick={() => toggleKey(lKey)}
                                    className="w-full flex items-center gap-3 px-3 py-2 pl-14 neu-rule-3 hover:bg-muted/30 transition-colors text-left">
                                    {lOpen ? <FiChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <FiChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                    <span className="shrink-0 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white" title="Líder">
                                      {initials(lg.leaderName)}
                                    </span>
                                    <div className="min-w-0 flex-1 flex items-center gap-1.5">
                                      <div className="text-[13px] font-semibold text-foreground truncate">{lg.leaderName}</div>
                                      {lg.tier === 'C' && (
                                        <span className="shrink-0 text-[9px] px-1 py-0.5 rounded border font-bold bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30"
                                          title={`Tier C — respondeu ${lg.tierRate != null ? Math.round(lg.tierRate * 100) : 0}% das últimas ${lg.tierSample ?? 0} cobranças (90 dias). Cobrança automática pausada pra proteger a qualidade do número; cobre manualmente ou troque a líder.`}>
                                          tier C
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 text-[11px]">
                                      <span className="text-muted-foreground" title="Leads recebidos no período">{lg.leads} lead{lg.leads === 1 ? '' : 's'}</span>
                                      <span className="text-emerald-600 dark:text-emerald-400" title="Taxa de resposta">{lg.responsePct}%</span>
                                      {lg.converted > 0 && <span className="text-purple-600 dark:text-purple-400" title="Convertidos (Oracle)">✦ {lg.converted}</span>}
                                      {lg.overdue > 0 && (
                                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title="Atrasados/escalados">
                                          <FiAlertTriangle className="w-3 h-3" />{lg.overdue}
                                        </span>
                                      )}
                                    </div>
                                  </button>

                                  {/* Consultores/leads da líder */}
                                  {lOpen && (
                                    <div className="divide-y divide-border">
                                      {lg.items.map(it => (
                                        <button key={it.assignmentId} onClick={() => openDetail(it)}
                                          className="w-full flex items-center gap-3 px-3 py-2 pl-20 neu-rule-4 hover:bg-muted/30 transition-colors text-left">
                                          <div className="min-w-0 flex-1">
                                            <div className="text-[13px] text-foreground truncate">{it.leadName}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                              {it.leadPhone} · encaminhado {fmtDateTime(it.assignedAt)}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2 shrink-0">
                                            <CountdownPill item={it} />
                                            <StageChip it={it} />
                                            {it.leaderOutcome && OUTCOME_META[it.leaderOutcome] && (
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium neu-tag ${OUTCOME_META[it.leaderOutcome].cls}`}>
                                                {OUTCOME_META[it.leaderOutcome].label}
                                              </span>
                                            )}
                                            {it.convertedOracle && (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded border font-bold bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30" title="Convertido (Oracle)">✦ Convertido</span>
                                            )}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium neu-tag ${STATUS_META[it.status].cls}`}>
                                              {STATUS_META[it.status].label}
                                            </span>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Drawer de detalhe / timeline */}
      {detailItem && (
        <>
          <div className="fixed inset-0 z-40 glass-overlay" onClick={() => setDetailItem(null)} />
          <div className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] glass-panel neu-drawer neu-forms flex flex-col">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground truncate">{detailItem.leadName}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {detailItem.leadPhone} · líder {detailItem.leaderName}
                </div>
              </div>
              <button onClick={() => setDetailItem(null)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-border grid grid-cols-2 gap-2 text-[11px]">
              <div><span className="text-muted-foreground">Status</span><div><span className={`mt-0.5 inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium neu-tag ${STATUS_META[detailItem.status].cls}`}>{STATUS_META[detailItem.status].label}</span></div></div>
              <div><span className="text-muted-foreground">Prazo</span><div className="text-foreground">{fmtDateTime(detailItem.deadlineAt)}</div></div>
              <div><span className="text-muted-foreground">Desfecho líder</span><div className="text-foreground">{detailItem.leaderOutcome && OUTCOME_META[detailItem.leaderOutcome] ? OUTCOME_META[detailItem.leaderOutcome].label : '—'}</div></div>
              <div><span className="text-muted-foreground">Conversão (Oracle)</span><div className="text-foreground">{detailItem.convertedOracle ? 'Convertido ✦' : 'Não'}</div></div>
              <div><span className="text-muted-foreground">Lembretes enviados</span><div className="text-foreground">{detailItem.nudgeCount}</div></div>
              <div><span className="text-muted-foreground">Tempo de resposta</span><div className="text-foreground">{detailItem.responseHours != null ? `${detailItem.responseHours}h` : '—'}</div></div>
              {detailItem.stage != null && (
                <div className="col-span-2"><span className="text-muted-foreground">Funil (3 etapas)</span><div className="mt-0.5"><StageChip it={detailItem} /></div></div>
              )}
            </div>
            {/* Ações manuais (diretoria) */}
            <div className="px-4 py-3 border-b border-border space-y-2.5">
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Ações</div>
              {/* Funil 3 etapas + registro manual — substituiu os botões de
                  desfecho único "Deu certo/Não deu/Tentando" (2026-07-10). */}
              <LeaderStageControl
                f={{ stage: detailItem.stage, contacted: detailItem.contacted, explained: detailItem.explained, interest: detailItem.interest }}
                status={detailItem.status} nudgeCount={detailItem.nudgeCount}
                leaderOutcome={detailItem.leaderOutcome} leaderOutcomeReason={detailItem.leaderOutcomeReason}
                busy={actionBusy}
                onReply={markStage}
              />
              <div className="flex gap-1.5">
                <input value={actionNote} onChange={e => setActionNote(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveNote() }}
                  placeholder="Anotação…"
                  className="flex-1 min-w-0 h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground placeholder:text-muted-foreground/70" />
                <button onClick={saveNote} disabled={actionBusy || !actionNote.trim()}
                  className="h-8 px-3 text-[11px] font-medium rounded-md border border-border neu-convex text-foreground hover:bg-muted/50 disabled:opacity-40 transition-colors shrink-0">
                  Salvar nota
                </button>
              </div>
              {detailItem.autoExpiry && detailItem.status === 'overdue' ? (
                <button onClick={() => postAction('notify-expired', {})} disabled={actionBusy}
                  title="Avisar a líder que o prazo de 24h expirou (template de utilidade). Pinga a líder e sobe a conversa no topo do inbox do atendimento."
                  className="w-full h-8 text-[11px] font-medium rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition-colors neu-relief inline-flex items-center justify-center gap-1.5">
                  <FiClock className="w-3.5 h-3.5" /> Notificar expirado
                </button>
              ) : (
                <button onClick={() => postAction('resend-nudge', {})} disabled={actionBusy}
                  title="Reenviar a pergunta da etapa atual à líder pelo WhatsApp. Pinga a líder e sobe a conversa no topo do inbox do atendimento."
                  className="w-full h-8 text-[11px] font-medium rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:bg-sky-500/20 disabled:opacity-40 transition-colors neu-relief inline-flex items-center justify-center gap-1.5">
                  <FiSend className="w-3.5 h-3.5" /> Reenviar cobrança
                </button>
              )}
              <button onClick={() => detailItem && setReassignFor(detailItem)} disabled={actionBusy}
                title="Encaminhar este lead a OUTRA líder (a primeira não retornou). Abre a seleção de líder por proximidade; o relógio de 24h reinicia para a nova líder."
                className="w-full h-8 text-[11px] font-medium rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 disabled:opacity-40 transition-colors neu-relief inline-flex items-center justify-center gap-1.5">
                <FiRepeat className="w-3.5 h-3.5" /> Trocar líder
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Histórico</div>
              {detailLoading && <div className="text-xs text-muted-foreground">Carregando…</div>}
              {!detailLoading && detailEvents.length === 0 && (
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                  <FiInfo className="w-3.5 h-3.5" />
                  Sem eventos registrados ainda{!detailItem.hasFollowupRow ? ' (atribuição anterior ao acompanhamento)' : ''}.
                </div>
              )}
              <ol className="relative border-l border-border ml-1 space-y-3">
                {detailEvents.map(ev => (
                  <li key={ev.id} className="ml-3">
                    <span className="absolute -left-[5px] w-2 h-2 rounded-full bg-purple-500" />
                    <div className="text-[12px] text-foreground">{EVENT_LABEL[ev.type] || ev.type}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtDateTime(ev.ts)}{ev.actor ? ` · ${ev.actor}` : ''}</div>
                    {ev.detail && (() => {
                      try { const o = JSON.parse(ev.detail); const s = o.text || o.leader_name || o.reason || ''
                        return s ? <div className="text-[11px] text-muted-foreground mt-0.5">{s}</div> : null
                      } catch { return <div className="text-[11px] text-muted-foreground mt-0.5">{ev.detail}</div> }
                    })()}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </>
      )}

      {/* Trocar líder: reaproveita o modal do inbox (busca por proximidade + guardas
          de janela Meta/telefone/reatribuição). Sem leadExtractedData/mensagens, o
          modal busca as mensagens da conversa e auto-detecta o CEP sozinho. */}
      {reassignFor && (
        <AssignLeaderModal
          isOpen={!!reassignFor}
          onClose={() => setReassignFor(null)}
          conversationId={reassignFor.conversationId}
          deviceId={reassignFor.deviceId}
          contactName={reassignFor.leadName}
          contactPhone={reassignFor.leadPhone}
          onAssign={() => { setReassignFor(null); setDetailItem(null); load() }}
        />
      )}

      {/* Modal de exportação — recorta o escopo antes de gerar o PDF (herdado do
          painel Relatório, fundido aqui em 2026-07-10). */}
      {showExport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center glass-overlay p-4"
          onClick={() => !exporting && setShowExport(false)}>
          <div className="glass-panel neu-drawer neu-forms rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <FiDownload className="w-4 h-4 text-purple-500" />
              <h3 className="text-sm font-semibold text-foreground">Exportar relatório em PDF</h3>
              <button onClick={() => setShowExport(false)} className="ml-auto text-muted-foreground hover:text-foreground" title="Fechar">
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Relatório</span>
                <select value={expTipo} onChange={e => setExpTipo(e.target.value as 'lideres' | 'anuncios')}
                  className="flex-1 text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="lideres">Desempenho das líderes (recorte por escopo)</option>
                  <option value="anuncios">Anúncios Meta → CRM (lead a lead, com custo por anúncio)</option>
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {expTipo === 'lideres' ? (
                  <>O PDF sai como <strong className="text-foreground">relatório individual por líder</strong> — lista de leads
                  (1º contato, CPF, nome, telefone, cidade, encaminhado), recortada pelo escopo escolhido abaixo.</>
                ) : (
                  <>O PDF sai como <strong className="text-foreground">relatório anúncio → CRM</strong> — de qual anúncio veio
                  cada lead, gasto e custo por lead, funil até o cadastro e desfecho da líder. Só o período se aplica.</>
                )}
              </p>
              {expTipo === 'lideres' && perfLoading && (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <FiLoader className="w-3.5 h-3.5 animate-spin" /> Carregando dados do período…
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Período</span>
                <input type="date" value={expFrom} max={expTo} onChange={e => setExpFrom(e.target.value)}
                  className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500" />
                <span className="text-[11px] text-muted-foreground">até</span>
                <input type="date" value={expTo} min={expFrom} onChange={e => setExpTo(e.target.value)}
                  className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500" />
              </div>
              {expTipo === 'lideres' && (<>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Cidade</span>
                <select value={expCidade} onChange={e => { setExpCidade(e.target.value); setExpDistrito(''); setExpGrupo(''); setExpLeaderId('') }}
                  className="flex-1 text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">Todas as cidades</option>
                  {expCidades.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Distrito</span>
                <select value={expDistrito} onChange={e => { setExpDistrito(e.target.value); setExpGrupo(''); setExpLeaderId('') }}
                  className="flex-1 text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">Todos os distritos</option>
                  {expDistritos.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Grupo</span>
                <select value={expGrupo} onChange={e => { setExpGrupo(e.target.value); setExpLeaderId('') }}
                  className="flex-1 text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">Todos os grupos</option>
                  {expGrupos.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Líder</span>
                <select value={expLeaderId} onChange={e => setExpLeaderId(e.target.value)}
                  className="flex-1 text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">Todas as líderes</option>
                  {expLideres.map(l => <option key={l.leaderId} value={l.leaderId}>{l.leaderName}{l.leaderCode ? ` (${l.leaderCode})` : ''}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer select-none"
                title="Página vertical estreita com leads em cards — ideal pra ler no celular (WhatsApp/Telegram). Desmarque pra A4 retrato, melhor pra imprimir em papel.">
                <input type="checkbox" checked={expMobile} onChange={e => setExpMobile(e.target.checked)}
                  className="accent-purple-600" />
                Otimizar para celular (página vertical)
              </label>
              </>)}
            </div>
            <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
              <button onClick={() => setShowExport(false)} disabled={exporting}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">
                Cancelar
              </button>
              <button onClick={generateExport} disabled={exporting || (expTipo === 'lideres' && perfLoading)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity">
                {exporting ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />}
                {exporting ? 'Gerando...' : 'Gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
